import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface Alert {
  id: number;
  type: AlertType;
  message: string;
  title?: string;
  duration?: number;
}

interface AlertOptions {
  duration?: number;
  [key: string]: any;
}

interface AlertContextType {
  alerts: Alert[];
  addAlert: (alert: Omit<Alert, 'id'>) => number;
  removeAlert: (id: number) => void;
  clearAllAlerts: () => void;
  showSuccess: (message: string, options?: AlertOptions) => number;
  showError: (message: string, options?: AlertOptions) => number;
  showWarning: (message: string, options?: AlertOptions) => number;
  showInfo: (message: string, options?: AlertOptions) => number;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const removeAlert = useCallback((id: number): void => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  const addAlert = useCallback((alert: Omit<Alert, 'id'>): number => {
    const id = Date.now() + Math.random();
    const newAlert: Alert = { id, ...alert };

    setAlerts(prev => [...prev, newAlert]);

    if (alert.duration !== 0) {
      setTimeout(() => {
        removeAlert(id);
      }, alert.duration || 5000);
    }

    return id;
  }, [removeAlert]);

  const clearAllAlerts = useCallback((): void => {
    setAlerts([]);
  }, []);

  const showSuccess = useCallback((message: string, options: AlertOptions = {}): number => {
    return addAlert({
      type: 'success',
      message,
      ...options
    });
  }, [addAlert]);

  const showError = useCallback((message: string, options: AlertOptions = {}): number => {
    return addAlert({
      type: 'error',
      message,
      duration: 7000,
      ...options
    });
  }, [addAlert]);

  const showWarning = useCallback((message: string, options: AlertOptions = {}): number => {
    return addAlert({
      type: 'warning',
      message,
      ...options
    });
  }, [addAlert]);

  const showInfo = useCallback((message: string, options: AlertOptions = {}): number => {
    return addAlert({
      type: 'info',
      message,
      ...options
    });
  }, [addAlert]);

  const value: AlertContextType = {
    alerts,
    addAlert,
    removeAlert,
    clearAllAlerts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};
